import apiCall, { endpoints } from './api';

/**
 * Service pour la gestion du profil utilisateur
 * ✅ Utilise les endpoints dédiés du AuthController
 */
export const profileService = {
  /**
   * ✅ Récupérer le profil de l'utilisateur connecté
   */
  getProfile: async () => {
    try {
      console.log('👤 Récupération du profil utilisateur...');
      
      const response = await apiCall.get(endpoints.auth.profile);
      console.log('📥 Réponse profil:', response);
      
      if (response.success) {
        console.log('✅ Profil récupéré:', response.data);
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors de la récupération du profil');
      }
      
    } catch (error) {
      console.error('❌ Erreur récupération profil:', error);
      
      // Gestion spécifique des erreurs d'authentification
      if (error.response?.status === 401) {
        return {
          success: false,
          message: 'Session expirée. Veuillez vous reconnecter.',
          requiresAuth: true
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la récupération du profil'
      };
    }
  },

  /**
   * ✅ Mettre à jour le profil utilisateur
   */
  updateProfile: async (profileData) => {
    try {
      console.log('💾 Mise à jour du profil:', profileData);
      
      // Validation des données avant envoi
      if (!profileData) {
        throw new Error('Données de profil manquantes');
      }

      const requiredFields = ['first_name', 'last_name', 'email'];
      const missingFields = requiredFields.filter(field => !profileData[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Champs obligatoires manquants: ${missingFields.join(', ')}`);
      }

      // Nettoyage des données
      const cleanedData = {
        firstName: profileData.first_name.trim(),
        lastName: profileData.last_name.trim(),
        email: profileData.email.trim().toLowerCase(),
        phone: profileData.phone ? profileData.phone.trim() : null
      };

      const response = await apiCall.put(endpoints.auth.profile, cleanedData);
      console.log('📥 Réponse mise à jour profil:', response);
      
      if (response.success) {
        console.log('✅ Profil mis à jour:', response.data);
        
        // Mettre à jour le cache localStorage
        const currentUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const updatedUserInfo = { ...currentUserInfo, ...response.data };
        localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
        
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors de la mise à jour du profil');
      }
      
    } catch (error) {
      console.error('❌ Erreur mise à jour profil:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la mise à jour du profil'
      };
    }
  },

  /**
   * ✅ Changer le mot de passe
   */
  changePassword: async (passwordData) => {
    try {
      console.log('🔒 Changement de mot de passe...');
      
      // Validation des données
      if (!passwordData.currentPassword || !passwordData.newPassword) {
        throw new Error('Mot de passe actuel et nouveau mot de passe requis');
      }

      if (passwordData.newPassword.length < 6) {
        throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      }

      const response = await apiCall.post(endpoints.auth.changePassword, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      console.log('📥 Réponse changement mot de passe:', response);
      
      if (response.success) {
        console.log('✅ Mot de passe changé avec succès');
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors du changement de mot de passe');
      }
      
    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors du changement de mot de passe'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Mettre à jour les préférences utilisateur
   */
  updatePreferences: async (preferences) => {
    try {
      console.log('⚙️ Mise à jour des préférences:', preferences);
      
      const response = await apiCall.put('/auth/preferences', preferences);
      console.log('📥 Réponse préférences:', response);
      
      if (response.success) {
        // Mettre à jour le cache des préférences
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors de la mise à jour des préférences');
      }
      
    } catch (error) {
      console.error('❌ Erreur mise à jour préférences:', error);
      
      // Pour l'instant, on simule le succès car l'endpoint n'existe peut-être pas encore
      if (error.response?.status === 404) {
        console.warn('⚠️ Endpoint préférences non implémenté, sauvegarde locale');
        localStorage.setItem('userPreferences', JSON.stringify(preferences));
        return {
          success: true,
          message: 'Préférences sauvegardées localement'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la mise à jour des préférences'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Récupérer les préférences utilisateur
   */
  getPreferences: async () => {
    try {
      console.log('⚙️ Récupération des préférences...');
      
      // D'abord essayer le serveur
      try {
        const response = await apiCall.get('/auth/preferences');
        if (response.success) {
          return response;
        }
      } catch (apiError) {
        console.warn('⚠️ Endpoint préférences non disponible, utilisation du cache local');
      }
      
      // Fallback vers le localStorage
      const localPreferences = localStorage.getItem('userPreferences');
      if (localPreferences) {
        return {
          success: true,
          data: JSON.parse(localPreferences),
          message: 'Préférences récupérées du cache local'
        };
      }
      
      // Valeurs par défaut
      const defaultPreferences = {
        emailNotifications: true,
        pushNotifications: false,
        theme: 'light',
        language: 'fr'
      };
      
      return {
        success: true,
        data: defaultPreferences,
        message: 'Préférences par défaut'
      };
      
    } catch (error) {
      console.error('❌ Erreur récupération préférences:', error);
      
      return {
        success: false,
        message: 'Erreur lors de la récupération des préférences'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Upload de photo de profil
   */
  uploadProfilePhoto: async (file) => {
    try {
      console.log('📸 Upload photo de profil...');
      
      if (!file) {
        throw new Error('Aucun fichier sélectionné');
      }

      // Validation du fichier
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Format de fichier non supporté. Utilisez JPG, PNG ou GIF.');
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Le fichier est trop volumineux. Taille maximum: 5MB.');
      }

      const formData = new FormData();
      formData.append('profilePhoto', file);

      const response = await apiCall.post('/auth/profile/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('📥 Réponse upload photo:', response);
      
      if (response.success) {
        // Mettre à jour le cache utilisateur avec la nouvelle photo
        const currentUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        currentUserInfo.profilePhotoUrl = response.data.photoUrl;
        localStorage.setItem('userInfo', JSON.stringify(currentUserInfo));
        
        return response;
      } else {
        throw new Error(response.message || 'Erreur lors de l\'upload de la photo');
      }
      
    } catch (error) {
      console.error('❌ Erreur upload photo:', error);
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de l\'upload de la photo'
      };
    }
  },

  /**
   * ✅ NOUVEAU: Obtenir l'historique des connexions
   */
  getLoginHistory: async () => {
    try {
      console.log('📋 Récupération historique des connexions...');
      
      const response = await apiCall.get('/auth/login-history');
      console.log('📥 Réponse historique:', response);
      
      return response;
      
    } catch (error) {
      console.error('❌ Erreur historique connexions:', error);
      
      // Si l'endpoint n'existe pas encore, retourner des données simulées
      if (error.response?.status === 404) {
        const currentUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
        return {
          success: true,
          data: [
            {
              id: 1,
              ip: '192.168.1.100',
              userAgent: navigator.userAgent,
              location: 'Dakar, Sénégal',
              loginTime: new Date().toISOString(),
              isCurrent: true
            }
          ],
          message: 'Historique simulé'
        };
      }
      
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erreur lors de la récupération de l\'historique'
      };
    }
  }
};

export default profileService;